import { Injectable } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';

@Injectable({
	providedIn: 'root'
})
export class RoutingService {

	routingEvent: EventEmitter<{ eventType: string, data?: any; timestamp: Date }> = new EventEmitter();

	private viewType: string | undefined;
    private lastSearchParams = new URLSearchParams(window.location.search || '');
      
	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private location: Location
	) {
		this.router.events.subscribe(e => {
			if (e instanceof NavigationEnd) {
				let currentViewType = this.getQueryParam('v');
				
				this.routingEvent.emit({
					eventType: 'navigationEnded',
					data: { viewType: currentViewType },
					timestamp: new Date()
				});
				
				if (currentViewType !== this.viewType) {
					this.viewType = currentViewType;

					this.routingEvent.emit({
						eventType: 'viewTypeChanged',
						data: { viewType: this.viewType },
						timestamp: new Date()
					});
				}
			}
		});
		this.initLastSearchParams();
	}
	
	getQueryParam(name: string): string | undefined {
		let value = undefined;
		const params = this.getQueryParams();
		if (params[name]) {
			value = params[name];
		}
		return value !== null ? value : undefined;
	}

	getQueryParams(): { [key: string]: string | null } {
		const params: { [key: string]: string | null } = {};

		// First, check for parameters in the hash part (after '#!')
		const hash = window.location.hash;
		if (hash && hash.includes('?')) {
			const queryString = hash.split('?')[1];  // Extract the part after '?'
			const urlParams = new URLSearchParams(queryString);

			// Populate params object with key-value pairs from the hash
			urlParams.forEach((value, key) => {
				params[key] = value;
			});
		}

		// Now check for standard query parameters (from the search part)
		const searchParams = window.location.search;
		if (searchParams) {
			const urlParams = new URLSearchParams(searchParams.substring(1));  // Skip the '?' part
			urlParams.forEach((value, key) => {
				params[key] = value;  // Add each query parameter to params
			});
		}

		// If the Angular route has query params, merge them with existing params (if needed)
		const routeParams = this.route.snapshot.queryParams;
		Object.assign(params, routeParams);  // This will add/overwrite values from the route snapshot

		return params;
	}
	
	/**
	 * Returns the detached state based on the parameter of the same name.
	 */
	isDetached(): boolean {
		let detached = (this.getQueryParam('detached')) ? true : false;
		return detached;
	}

	getSearchQueryParams(): URLSearchParams {
		const url = window.location.pathname;
		const queryString = window.location.hash.split('?')[1] || '';
		return new URLSearchParams(queryString);
	}
    
    initLastSearchParams(){
 	// Get the current URL
  	  const currentUrl = new URL(window.location.href);
	  this.lastSearchParams = new URLSearchParams(currentUrl.search || currentUrl.hash.split('?')[1] || '');
	  if (this.lastSearchParams.size == 0) {
		this.lastSearchParams = this.getSearchQueryParams();
	  }
	}
	
	updateSearchUrl(newParams: { [key: string]: string | null | undefined }, isReplace: boolean, isReload?: boolean) {
		const currentUrlParams = this.lastSearchParams.toString();
		if (!isReplace) {
			// Merge: Add new parameters while keeping the existing ones
			Object.keys(newParams).forEach(key => {
				if (newParams[key] == null || newParams[key] === '') {
					this.lastSearchParams.delete(key); // Remove empty/null params
				} else {
					if (newParams[key]) {
						this.lastSearchParams.set(key, newParams[key] != null ? String(newParams[key]) : '');
					}
				}
			});
		} else {
			// Replace: Remove all existing parameters, and only use new ones
			const updatedParams = new URLSearchParams();
			Object.keys(newParams).forEach(key => {
				if (newParams[key] && newParams[key] !== '') {
					updatedParams.set(key, newParams[key] != null ? String(newParams[key]) : '');
				}
			});
			// Now use the updatedParams (which contains only new parameters)
			this.lastSearchParams = new URLSearchParams(); // Remove all existing parameters
			updatedParams.forEach((value, key) => {
				this.lastSearchParams.set(key, value);
			});
		}
		let isNotify = (isReload) ? true : false;
		const isSameUrl = currentUrlParams === this.lastSearchParams.toString();

		if (isReload) {
			const queryParams = Object.fromEntries(this.lastSearchParams.entries());
			this.router.navigate(['/s'], {
				queryParams: queryParams
			});
		} else {
			if (!isSameUrl) {
				const queryParams = Object.fromEntries(this.lastSearchParams.entries());
				this.router.navigate(['/s'], {
					relativeTo: this.route, // important: stay on the same route
					queryParams: queryParams,
					//queryParamsHandling: 'merge',
					replaceUrl: true // avoids pushing to browser history
				});
			} else {
				console.log("Skip URL update, same URL");
			}
		}
	}

	isSearchRoute(): boolean {
		const isSearchRoute = this.router?.url?.startsWith('/s') || false;
		return isSearchRoute;
	}
}
